import { type Meta } from '../types/docs';
import { WORKSPACE_AGENT_ANALYTICS_CATEGORY_META_PARAM } from './workspaceAgentAnalytics';

export const WORKSPACE_AGENT_TASK_SUBMITTED_TYPES = [
  'manual_submit',
  'hero_chip',
  'marketing_url',
  'selection_menu',
  'edit_tags_modal',
  'actions_menu',
  'board_menu',
  'guided_flow',
  'announcement',
] as const;

/** Reused on actions_menu_task_id when type is guided_flow, instead of a separate flow id. */
export const WORKSPACE_AGENT_GUIDED_FLOW_ACTIONS_MENU_TASK_ID = 'guided_variants';

export const WORKSPACE_AGENT_GUIDED_FLOW_EDIT_IDS = [
  'smart_resize',
  'scale_or_crop',
  'watermark',
  'grayscale',
  'rounded_corners',
  'remove_background',
] as const;

export const WORKSPACE_AGENT_GUIDED_FLOW_DESTINATIONS = [
  'versions',
  'this_board',
  'new_subboard',
  'selected_board',
] as const;

export const WORKSPACE_AGENT_GUIDED_FLOW_FORMATS = ['original', 'jpg', 'png', 'webp'] as const;

export type WorkspaceAgentGuidedFlowEditId = (typeof WORKSPACE_AGENT_GUIDED_FLOW_EDIT_IDS)[number];

export type WorkspaceAgentGuidedFlowDestination = (typeof WORKSPACE_AGENT_GUIDED_FLOW_DESTINATIONS)[number];

export type WorkspaceAgentGuidedFlowFormat = (typeof WORKSPACE_AGENT_GUIDED_FLOW_FORMATS)[number];

export type WorkspaceAgentTaskSubmittedType = (typeof WORKSPACE_AGENT_TASK_SUBMITTED_TYPES)[number];

const META: Meta = {
  description: 'The user successfully submits a task in the workspace agent composer',
  name: 'agent_task_submitted',
  params: [
    WORKSPACE_AGENT_ANALYTICS_CATEGORY_META_PARAM,
    {
      description:
        'Whether the user typed manually, used a zero-state hero chip, launched a hero task from a selection menu or edit-tags modal promo, sent a prompt preloaded from a marketing deep link, submitted a prompt inserted from the Actions menu, launched the Board templates prompt from a board right-click menu, sent a prompt seeded by a guided flow, or sent a prompt from an announcement',
      name: 'type',
      required: true,
      type: 'enum',
      values: [...WORKSPACE_AGENT_TASK_SUBMITTED_TYPES],
    },
    {
      description: 'The id of the workspace agent session',
      name: 'session_id',
      required: true,
      type: 'string',
    },
    {
      description:
        'The Actions menu task whose prompt was sent when type is actions_menu. When type is guided_flow, the guided flow id (guided_variants) — there is no separate flow id.',
      name: 'actions_menu_task_id',
      required: false,
      type: 'string',
    },
    {
      description: 'The guided-flow tool whose prompt was seeded. Only set when type is guided_flow.',
      name: 'edit_id',
      required: false,
      type: 'enum',
      values: [...WORKSPACE_AGENT_GUIDED_FLOW_EDIT_IDS],
    },
    {
      description: 'Where the guided flow saved the variants. Only set when type is guided_flow.',
      name: 'destination',
      required: false,
      type: 'enum',
      values: [...WORKSPACE_AGENT_GUIDED_FLOW_DESTINATIONS],
    },
    {
      description: 'Whether the user typed a filename suffix in the guided flow. Only set when type is guided_flow.',
      name: 'has_suffix',
      required: false,
      type: 'boolean',
    },
    {
      description: 'The output format applied by the guided flow. Only set when type is guided_flow.',
      name: 'format',
      required: false,
      type: 'enum',
      values: [...WORKSPACE_AGENT_GUIDED_FLOW_FORMATS],
    },
    {
      description: 'Whether the serialized send content includes any skill monikers',
      name: 'contains_skill',
      required: true,
      type: 'boolean',
    },
    {
      description: 'Distinct skill ids present in the composer at submit (0 when contains_skill is false)',
      name: 'skill_count',
      required: true,
      type: 'number',
    },
    {
      description: 'Distinct skill ids in the composer at submit; omitted when contains_skill is false',
      name: 'skill_ids',
      required: false,
      type: 'array',
      values: ['string'],
    },
  ],
};

export default META;
